import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { ChecklistStatus, MarginBand, RecordLike } from '../../shared/checklistUtils';
import {
  getStatusLabel,
  isFiniteNumber,
  isPositiveNumber,
  normalizeString,
  readNumber,
  readString,
} from '../../shared/checklistUtils';

import type { EgsCapacityChecklistRow, EgsMarginChecklistRow } from './types';

const EGS_MARGIN_FIELD = 'Extra Guest Surcharges margin';
const EGS_CAPACITY_FIELD = 'EGS capacity';

const INCLUDED_CAPACITY_FIELDS = [
  'included_capacity',
  'included_guests',
  'max_included_guests',
  'max_guests_included',
  'number_of_guests',
];

export function calculateMarginPercentage(costPrice?: number, sellPrice?: number) {
  if (!isPositiveNumber(sellPrice) || !isFiniteNumber(costPrice)) {
    return undefined;
  }

  return ((sellPrice - costPrice) / sellPrice) * 100;
}

export function getMarginBand(margin?: number): MarginBand | undefined {
  if (!isFiniteNumber(margin)) {
    return undefined;
  }

  if (margin <= 10) {
    return 'red';
  }

  if (margin >= 19) {
    return 'green';
  }

  return 'yellow';
}

export function getMarginStatus(marginBand?: MarginBand): ChecklistStatus {
  if (!marginBand) {
    return 'requires approval';
  }

  return marginBand === 'green' ? 'approved' : 'requires approval';
}

export function buildEgsMarginRows(
  relatedExtraGuestSurcharges: Array<OpportunityRelatedExtraGuestSurcharge> = [],
): Array<EgsMarginChecklistRow> {
  return relatedExtraGuestSurcharges.map((relatedSurcharge) => {
    const costPrice =
      readNumber(relatedSurcharge, ['converted_cost__c']) ?? readNumber(relatedSurcharge, ['cost_price__c']);
    const sellPrice = readNumber(relatedSurcharge, ['sell_price_aud__c']);
    const sourceMargin = readNumber(relatedSurcharge, ['margin__c']);
    const margin = sourceMargin ?? calculateMarginPercentage(costPrice, sellPrice);
    const marginBand = getMarginBand(margin);
    const status = getMarginStatus(marginBand);

    return {
      ageRange: normalizeString(relatedSurcharge?.age_range__c),
      beddingType: normalizeString(relatedSurcharge?.other_extra_bedding_type__c || relatedSurcharge?.bedding_type__c),
      collectedBy: normalizeString(relatedSurcharge?.collected_by__c),
      id: `margin:extra_guest_surcharge__c:${String(relatedSurcharge.id)}`,
      costPrice,
      currencyCode: 'AUD',
      extraGuestType: normalizeString(relatedSurcharge?.extra_guest_type__c),
      field: EGS_MARGIN_FIELD,
      inclusions: normalizeString(relatedSurcharge?.inclusions__c),
      margin,
      marginBand,
      name: normalizeString(relatedSurcharge?.name) ?? String(relatedSurcharge.id),
      opportunityName: normalizeString(relatedSurcharge?.opportunity__c),
      sellPrice,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

export function buildEgsCapacityRows({
  hasEgsMarginRows,
  packages,
  propertyRoomTypes,
  relatedExtraGuestSurcharges,
}: {
  hasEgsMarginRows?: boolean;
  packages?: Array<App.Package>;
  propertyRoomTypes?: Array<App.RoomType>;
  relatedExtraGuestSurcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
}): Array<EgsCapacityChecklistRow> {
  const hasConfiguredEgsMarginRows = hasEgsMarginRows ?? (relatedExtraGuestSurcharges?.length ?? 0) > 0;

  if (!hasConfiguredEgsMarginRows) {
    return [];
  }

  return (packages ?? []).map((offerPackage, index) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const roomTypeId = readString(packageRecord, ['fk_room_type_id']);
    const roomRateId = readString(packageRecord, ['fk_room_rate_id']);
    const roomType = propertyRoomTypes?.find((room) => String(room.id) === roomTypeId);
    const roomRate = findRoomRate(roomType, roomRateId);
    const includedCapacity =
      readNumber(packageRecord, INCLUDED_CAPACITY_FIELDS) ??
      readIncludedGuestsCapacity(roomRate) ??
      readNumber((roomType ?? {}) as RecordLike, ['max_included_guests']);
    const maxRoomCapacity = getMaxRoomCapacity(roomRate);
    // No Salesforce field carries "extra guest capacity" directly — it's the headroom
    // between the room rate's own max capacity and what's already included, so it's
    // derived rather than read, and can never itself disagree with the other two.
    const egsCapacity =
      isFiniteNumber(includedCapacity) && isFiniteNumber(maxRoomCapacity)
        ? maxRoomCapacity - includedCapacity
        : undefined;
    const status =
      isFiniteNumber(includedCapacity) && isFiniteNumber(maxRoomCapacity) ? 'approved' : 'requires approval';

    return {
      egsCapacity,
      field: EGS_CAPACITY_FIELD,
      id: `capacity:package:${String(
        offerPackage.id_salesforce_external ?? offerPackage.le_package_id ?? index,
      )}:room_type:${roomTypeId ?? ''}:room_rate:${roomRateId ?? ''}`,
      includedCapacity,
      maxRoomCapacity,
      name: offerPackage.deal_option_name || offerPackage.name || `Package ${index + 1}`,
      roomRateId,
      roomName: roomType?.name || 'Room not mapped',
      roomTypeId,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function getMaxRoomCapacity(roomRate?: App.RoomRate) {
  const capacities = (roomRate as unknown as RecordLike | undefined)?.capacities;

  if (!Array.isArray(capacities)) {
    return undefined;
  }

  return getMaxDefinedNumber(capacities.map(getCapacityTotal));
}

function findRoomRate(roomType?: App.RoomType, roomRateId?: string) {
  if (!roomType || !roomRateId || !Array.isArray(roomType.room_rates)) {
    return undefined;
  }

  return roomType.room_rates.find((roomRate) => String(roomRate.id) === roomRateId);
}

function readIncludedGuestsCapacity(roomRate?: App.RoomRate) {
  const includedGuests = (roomRate as unknown as RecordLike | undefined)?.included_guests;

  if (!Array.isArray(includedGuests)) {
    return undefined;
  }

  return getMaxDefinedNumber(includedGuests.map(getCapacityTotal));
}

function getCapacityTotal(value: unknown) {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }

  const record = value as RecordLike;
  const explicitTotal = readNumber(record, ['total', 'total_guests', 'max_guests']);

  if (isFiniteNumber(explicitTotal)) {
    return explicitTotal;
  }

  const adults = readNumber(record, ['adults']) ?? 0;
  const children = readNumber(record, ['children']) ?? 0;
  const teenagers = readNumber(record, ['teenagers']) ?? 0;

  return adults + children + teenagers;
}

function getMaxDefinedNumber(values: Array<number | undefined>) {
  const numericValues = values.filter(isFiniteNumber);

  return numericValues.length > 0 ? Math.max(...numericValues) : undefined;
}
